<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import AgentBranchAccessAPI from 'dashboard/api/agentBranchAccess';

// V1 · Permisos de Sucursal por Agente · 2026-05-14 (Victor)
// Page: grid de agentes con chips de sucursales · click abre modal con
// checkboxes · save actualiza accessible_branch_ids + auto-sync inbox_members.

const router = useRouter();
const { isAdminOrSuperAdmin } = usePermissions();

const users = ref([]);
const branches = ref([]);
const isLoading = ref(true);
const search = ref('');

// Modal state
const modalOpen = ref(false);
const editingUser = ref(null);
const selectedBranchIds = ref([]);
const isSaving = ref(false);

const branchById = computed(() => {
  const map = {};
  branches.value.forEach(b => { map[b.id] = b; });
  return map;
});

const filteredUsers = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return users.value;
  return users.value.filter(u =>
    (u.name || '').toLowerCase().includes(q)
    || (u.email || '').toLowerCase().includes(q)
  );
});

function branchCodeClass(code) {
  const c = (code || '').toLowerCase();
  if (c === 'pers') return 'bc-pers';
  if (c === 'sde') return 'bc-sde';
  if (c === 'sdo') return 'bc-sdo';
  if (c === 'lrm') return 'bc-lrm';
  if (c === 'stg') return 'bc-stg';
  return 'bc-other';
}

function agentInitials(name) {
  return (name || '?').trim().split(/\s+/).slice(0, 2)
    .map(w => w.charAt(0).toUpperCase()).join('') || '?';
}

function userBranchChips(user) {
  if (user.super_admin && (!user.accessible_branch_ids || user.accessible_branch_ids.length === 0)) {
    return [{ id: 'ALL', code: 'TODAS', name: 'Acceso total (super_admin)', allAccess: true }];
  }
  const ids = Array.isArray(user.accessible_branch_ids) ? user.accessible_branch_ids : [];
  if (ids.length === 0) {
    return [{ id: 'NONE', code: 'Sin sucursal', name: 'Bloqueado', empty: true }];
  }
  return ids.map(id => branchById.value[id]).filter(Boolean);
}

async function loadAll() {
  isLoading.value = true;
  try {
    const data = await AgentBranchAccessAPI.getAll();
    users.value = Array.isArray(data.users) ? data.users : [];
    branches.value = Array.isArray(data.branches) ? data.branches : [];
  } catch (e) {
    useAlert('No se pudo cargar la lista · revisa que tengas permiso de super admin');
  } finally {
    isLoading.value = false;
  }
}

function openModal(user) {
  editingUser.value = user;
  selectedBranchIds.value = Array.isArray(user.accessible_branch_ids)
    ? [...user.accessible_branch_ids]
    : [];
  modalOpen.value = true;
}

function closeModal() {
  modalOpen.value = false;
  editingUser.value = null;
  selectedBranchIds.value = [];
}

function toggleBranch(branchId) {
  if (editingUser.value?.super_admin && (!editingUser.value.accessible_branch_ids || editingUser.value.accessible_branch_ids.length === 0)) {
    return; // disabled for super_admin with all-access
  }
  if (selectedBranchIds.value.includes(branchId)) {
    selectedBranchIds.value = selectedBranchIds.value.filter(b => b !== branchId);
  } else {
    selectedBranchIds.value = [...selectedBranchIds.value, branchId];
  }
}

const modalIsSuperAdminReadOnly = computed(() => {
  return editingUser.value?.super_admin
    && (!editingUser.value?.accessible_branch_ids || editingUser.value.accessible_branch_ids.length === 0);
});

const hasChanges = computed(() => {
  if (!editingUser.value) return false;
  const orig = Array.isArray(editingUser.value.accessible_branch_ids) ? editingUser.value.accessible_branch_ids : [];
  const cur = selectedBranchIds.value;
  if (orig.length !== cur.length) return true;
  const sortedOrig = [...orig].sort();
  const sortedCur = [...cur].sort();
  return sortedOrig.some((v, i) => v !== sortedCur[i]);
});

async function saveBranchAccess() {
  if (!editingUser.value || isSaving.value || !hasChanges.value) return;
  isSaving.value = true;
  try {
    const updated = await AgentBranchAccessAPI.update(editingUser.value.id, selectedBranchIds.value);
    const idx = users.value.findIndex(u => u.id === updated.id);
    if (idx !== -1) users.value.splice(idx, 1, updated);
    useAlert('✅ Permisos actualizados · inbox_members sincronizados');
    closeModal();
  } catch (e) {
    useAlert('Error al guardar · revisa tu permiso de super admin');
  } finally {
    isSaving.value = false;
  }
}

onMounted(() => {
  if (!isAdminOrSuperAdmin.value) {
    router.replace({ name: 'settings_home' });
    return;
  }
  loadAll();
});
</script>

<template>
  <div class="aba-wrap">
    <div class="aba-header">
      <div>
        <h1 class="aba-title">Permisos de Sucursal por Agente</h1>
        <p class="aba-subtitle">
          Click en cualquier agente para configurar de qué sucursales recibirá chats vía round-robin.
          Al guardar se actualiza <code>accessible_branch_ids</code> y se sincronizan los <code>inbox_members</code> automáticamente.
        </p>
      </div>
      <div class="aba-search">
        <span class="aba-search-icon">🔍</span>
        <input v-model="search" placeholder="Buscar agente por nombre o email…">
      </div>
    </div>

    <div v-if="isLoading" class="aba-loading">Cargando…</div>

    <template v-else>
      <div v-if="filteredUsers.length === 0" class="aba-empty">
        {{ search ? 'Ningún agente coincide con la búsqueda.' : 'No hay agentes en la cuenta.' }}
      </div>

      <div v-else class="aba-grid">
        <button
          v-for="user in filteredUsers"
          :key="user.id"
          type="button"
          class="aba-row"
          @click="openModal(user)"
        >
          <div class="aba-avatar">
            <img v-if="user.avatar_url" :src="user.avatar_url" alt="" />
            <span v-else>{{ agentInitials(user.name) }}</span>
          </div>
          <div class="aba-info">
            <div class="aba-name">{{ user.name }}</div>
            <div class="aba-meta">
              {{ user.email }} · {{ user.super_admin ? 'Super Admin' : (user.role === 'administrator' ? 'Admin' : 'Agent') }}
            </div>
          </div>
          <div class="aba-chips">
            <span
              v-for="chip in userBranchChips(user)"
              :key="`${user.id}-${chip.id}`"
              class="aba-chip"
              :class="[
                branchCodeClass(chip.code),
                { 'aba-chip-all': chip.allAccess, 'aba-chip-none': chip.empty }
              ]"
            >
              {{ chip.code }}
            </span>
          </div>
          <div class="aba-edit-icon">✏</div>
        </button>
      </div>
    </template>

    <!-- ===== MODAL ===== -->
    <div v-if="modalOpen" class="aba-modal-backdrop" @click.self="closeModal">
      <div class="aba-modal-card">
        <div class="aba-modal-header">
          <div class="aba-modal-avatar">
            <img v-if="editingUser?.avatar_url" :src="editingUser.avatar_url" alt="" />
            <span v-else>{{ agentInitials(editingUser?.name) }}</span>
          </div>
          <div class="aba-modal-h-text">
            <div class="aba-modal-h-name">{{ editingUser?.name }}</div>
            <div class="aba-modal-h-meta">
              {{ editingUser?.email }} ·
              {{ editingUser?.super_admin ? 'Super Admin' : (editingUser?.role === 'administrator' ? 'Admin' : 'Agent') }}
            </div>
          </div>
          <button type="button" class="aba-modal-close" @click="closeModal">✕</button>
        </div>

        <div class="aba-modal-body">
          <div class="aba-modal-section-title">Sucursales con permiso a recibir mensajes</div>
          <div class="aba-modal-section-hint">
            <template v-if="modalIsSuperAdminReadOnly">
              <strong>Super admin con acceso total.</strong> Por convención, los super_admins ven todas las sucursales sin necesidad de marcarlas. Si necesitas restringir a este usuario, primero quítale el rol de super_admin desde Ajustes → Agentes.
            </template>
            <template v-else>
              Marca las sucursales de las cuales este agente recibirá chats automáticamente. Al guardar se actualiza su acceso y queda agregado/removido como miembro de los inboxes de cada sucursal.
            </template>
          </div>

          <div class="aba-branch-list">
            <div
              v-for="branch in branches"
              :key="`mb-${branch.id}`"
              class="aba-branch-item"
              :class="{
                checked: selectedBranchIds.includes(branch.id) || modalIsSuperAdminReadOnly,
                disabled: modalIsSuperAdminReadOnly
              }"
              @click="toggleBranch(branch.id)"
            >
              <div class="aba-branch-tile" :class="branchCodeClass(branch.code)">
                {{ branch.code }}
              </div>
              <div class="aba-branch-info">
                <div class="aba-branch-name">{{ branch.name }}</div>
                <div class="aba-branch-meta">
                  {{ branch.is_personal ? 'Personal' : 'Sucursal' }} · {{ branch.inbox_count }} {{ branch.inbox_count === 1 ? 'inbox' : 'inboxes' }}
                </div>
              </div>
              <div class="aba-branch-check">
                <span v-if="selectedBranchIds.includes(branch.id) || modalIsSuperAdminReadOnly">✓</span>
              </div>
            </div>
          </div>
        </div>

        <div class="aba-modal-actions">
          <button type="button" class="aba-btn aba-btn-cancel" @click="closeModal">Cancelar</button>
          <button
            type="button"
            class="aba-btn aba-btn-save"
            :disabled="!hasChanges || isSaving || modalIsSuperAdminReadOnly"
            @click="saveBranchAccess"
          >
            {{ isSaving ? 'Guardando…' : '💾 Guardar permisos' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.aba-wrap {
  padding: 24px 32px 80px;
  max-width: 1100px;
  margin: 0 auto;
  font-family: -apple-system, 'SF Pro Display', 'Inter', sans-serif;
}

.aba-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 22px;
}
.aba-title {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #f4d57a 0%, #c89b3f 50%, #8b6914 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 6px;
}
.aba-subtitle {
  font-size: 13px;
  color: #8a7960;
  max-width: 640px;
  line-height: 1.5;
  margin: 0;
}
.aba-subtitle code {
  background: rgba(196,150,60,0.15);
  padding: 1px 5px;
  border-radius: 4px;
  color: #f4d57a;
  font-size: 11.5px;
}
.aba-search {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(20,15,10,0.6);
  border: 1px solid rgba(196,150,60,0.22);
  border-radius: 10px;
  padding: 8px 12px;
  min-width: 260px;
}
.aba-search-icon { font-size: 13px; }
.aba-search input {
  background: transparent;
  border: 0;
  outline: 0;
  color: #c8b78f;
  font-size: 12.5px;
  flex: 1;
  font-family: inherit;
}

.aba-loading,
.aba-empty {
  padding: 50px;
  text-align: center;
  color: #8a7960;
  font-size: 13px;
}

.aba-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (max-width: 800px) { .aba-grid { grid-template-columns: 1fr; } }

.aba-row {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(20,15,10,0.5);
  border: 1px solid rgba(196,150,60,0.15);
  border-radius: 11px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.18s;
  text-align: left;
  font-family: inherit;
  color: inherit;
}
.aba-row:hover {
  border-color: rgba(196,150,60,0.45);
  background: rgba(40,30,18,0.55);
  transform: translateY(-1px);
}
.aba-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c89b3f, #8b6914);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  overflow: hidden;
}
.aba-avatar img {
  width: 100%; height: 100%; object-fit: cover;
}
.aba-info {
  flex: 1;
  min-width: 0;
}
.aba-name {
  color: #e8dcc4;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aba-meta {
  color: #8a7960;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aba-chips {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
  flex-wrap: wrap;
  max-width: 220px;
  justify-content: flex-end;
}
.aba-chip {
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
}
.bc-pers { background: rgba(124,58,237,0.18); color: #c4b5fd; border: 1px solid rgba(124,58,237,0.4); }
.bc-sde  { background: rgba(40,180,90,0.18);  color: #6cd497; border: 1px solid rgba(40,180,90,0.4); }
.bc-sdo  { background: rgba(245,158,11,0.18); color: #fbbf24; border: 1px solid rgba(245,158,11,0.4); }
.bc-lrm  { background: rgba(168,85,247,0.18); color: #d8b4fe; border: 1px solid rgba(168,85,247,0.4); }
.bc-stg  { background: rgba(244,63,94,0.18);  color: #fda4af; border: 1px solid rgba(244,63,94,0.4); }
.bc-other { background: rgba(196,150,60,0.18); color: #f4d57a; border: 1px solid rgba(196,150,60,0.4); }
.aba-chip-all {
  background: linear-gradient(135deg, rgba(196,150,60,0.22), rgba(196,150,60,0.06));
  color: #f4d57a;
  border: 1px solid rgba(196,150,60,0.5);
}
.aba-chip-none {
  background: rgba(120,113,108,0.15);
  color: #8a7960;
  border: 1px dashed rgba(120,113,108,0.4);
  font-style: italic;
}
.aba-edit-icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgba(196,150,60,0.12);
  border: 1px solid rgba(196,150,60,0.3);
  color: #f4d57a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
}

/* ====== Modal ====== */
.aba-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}
.aba-modal-card {
  width: 100%;
  max-width: 480px;
  background: linear-gradient(180deg, #14100b 0%, #0a0807 100%);
  border: 1.5px solid rgba(196,150,60,0.45);
  border-radius: 18px;
  box-shadow: 0 26px 60px rgba(0,0,0,0.75), 0 0 50px rgba(196,150,60,0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}
.aba-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(196,150,60,0.12);
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(180deg, rgba(196,150,60,0.08), transparent);
}
.aba-modal-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c89b3f, #8b6914);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 800;
  flex-shrink: 0;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(196,150,60,0.3);
}
.aba-modal-avatar img {
  width: 100%; height: 100%; object-fit: cover;
}
.aba-modal-h-text { flex: 1; min-width: 0; }
.aba-modal-h-name {
  color: #f4d57a;
  font-size: 16px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aba-modal-h-meta {
  color: #8a7960;
  font-size: 11px;
  margin-top: 3px;
}
.aba-modal-close {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(60,55,45,0.6);
  color: #c8b78f;
  border: 1px solid rgba(196,150,60,0.2);
  cursor: pointer;
  font-family: inherit;
}
.aba-modal-body {
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;
}
.aba-modal-section-title {
  color: #f4d57a;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}
.aba-modal-section-hint {
  color: #8a7960;
  font-size: 11.5px;
  line-height: 1.55;
  margin-bottom: 14px;
}
.aba-modal-section-hint strong { color: #f4d57a; }

.aba-branch-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.aba-branch-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(20,15,10,0.6);
  border: 1.5px solid rgba(196,150,60,0.18);
  border-radius: 10px;
  padding: 11px 13px;
  cursor: pointer;
  transition: all 0.18s;
}
.aba-branch-item:hover { border-color: rgba(196,150,60,0.42); }
.aba-branch-item.checked {
  background: linear-gradient(135deg, rgba(40,180,90,0.12), rgba(20,40,30,0.4));
  border-color: rgba(40,180,90,0.55);
}
.aba-branch-item.disabled {
  cursor: not-allowed;
  opacity: 0.85;
}
.aba-branch-tile {
  width: 36px; height: 36px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}
.aba-branch-info { flex: 1; min-width: 0; }
.aba-branch-name {
  color: #e8dcc4;
  font-size: 13px;
  font-weight: 600;
}
.aba-branch-meta {
  color: #8a7960;
  font-size: 10.5px;
  margin-top: 2px;
}
.aba-branch-check {
  width: 22px; height: 22px;
  border-radius: 6px;
  border: 1.5px solid rgba(196,150,60,0.4);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: all 0.18s;
}
.aba-branch-item.checked .aba-branch-check {
  background: linear-gradient(135deg, #4ade80, #16a34a);
  border-color: #4ade80;
  color: #0a0807;
  font-weight: 800;
  font-size: 13px;
}

.aba-modal-actions {
  display: flex;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid rgba(196,150,60,0.12);
  justify-content: flex-end;
  background: rgba(15,12,8,0.4);
}
.aba-btn {
  border: none;
  padding: 9px 18px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  letter-spacing: 0.3px;
  font-family: inherit;
}
.aba-btn-cancel {
  background: transparent;
  color: #8a7960;
  border: 1px solid rgba(196,150,60,0.25);
}
.aba-btn-save {
  background: linear-gradient(135deg, #f4d57a, #c89b3f);
  color: #1a1410;
  box-shadow: 0 4px 14px rgba(196,150,60,0.3);
}
.aba-btn-save:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
